import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import CookieConsent, { Cookies } from 'react-cookie-consent'
import { initializeAndTrack } from 'gatsby-plugin-gdpr-cookies'

const COOKIE_EXPIRY_DAYS = 365
const OPEN_PREFERENCES_EVENT = 'cookie-preferences:open'
const BANNER_BUTTON_CLASSES = 'button button-primary'
const BANNER_SECONDARY_BUTTON_CLASSES = 'button button-secondary'

// Add an entry here for each consented service (e.g. Google Tag Manager) as it's introduced.
// The cookie name should match whatever gatsby-plugin-gdpr-cookies is configured to check in gatsby-config.js.
const COOKIE_CATEGORIES = [
  {
    key: 'analytics',
    label: 'Analytics',
    cookieName: 'gatsby-gdpr-google-analytics',
    description: 'Lets us use Google Analytics to understand how visitors use the site.'
  }
]

export const openCookiePreferences = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT))
  }
}

const allChoices = (accepted) => COOKIE_CATEGORIES.reduce((acc, category) => ({ ...acc, [category.key]: accepted }), {})

const readChoices = () =>
  COOKIE_CATEGORIES.reduce((choices, category) => {
    choices[category.key] = Cookies.get(category.cookieName) === 'true'
    return choices
  }, {})

const writeChoices = (choices) => {
  COOKIE_CATEGORIES.forEach((category) => {
    Cookies.set(category.cookieName, choices[category.key] ? 'true' : 'false', { expires: COOKIE_EXPIRY_DAYS, sameSite: 'strict' })
  })
}

const CookiePreferencesModal = ({ choices, onChange, onSave, onClose }) => (
  <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-25 p-4'>
    <div className='w-full max-w-md rounded bg-white p-6 shadow-md'>
      <h2 className='mb-2 font-serif text-2xl'>Cookie preferences</h2>
      <p className='mb-4 text-sm text-gray-700'>Choose which cookies you're happy for us to use. You can change your mind at any time from the "Cookie Settings" link in the footer.</p>
      <ul className='mb-6 space-y-4'>
        {COOKIE_CATEGORIES.map((category) => (
          <li key={category.key} className='flex items-start justify-between gap-4'>
            <div>
              <div className='font-semibold'>{category.label}</div>
              <p className='text-sm text-gray-700'>{category.description}</p>
            </div>
            <input
              type='checkbox'
              className='mt-1 h-5 w-5 shrink-0'
              checked={choices[category.key]}
              onChange={(e) => onChange(category.key, e.target.checked)}
              aria-label={`${category.label} cookies`}
            />
          </li>
        ))}
      </ul>
      <div className='flex justify-end gap-2'>
        <button type='button' onClick={onClose} className='button button-secondary'>
          Cancel
        </button>
        <button type='button' onClick={onSave} className='button button-primary'>
          Save preferences
        </button>
      </div>
    </div>
  </div>
)

const CookiePreferencesBanner = ({ location }) => {
  const [showPreferences, setShowPreferences] = useState(false)
  const [choices, setChoices] = useState(readChoices)

  useEffect(() => {
    const handleOpen = () => {
      setChoices(readChoices())
      setShowPreferences(true)
    }
    window.addEventListener(OPEN_PREFERENCES_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handleOpen)
  }, [])

  // Widening consent (e.g. accepting) can take effect immediately via initializeAndTrack.
  // Narrowing it (e.g. switching analytics off after previously accepting) can't be undone without
  // a reload, since gatsby-plugin-gdpr-cookies has no way to unload a script it's already injected.
  const applyChoices = (nextChoices) => {
    const previousChoices = readChoices()
    writeChoices(nextChoices)
    setShowPreferences(false)

    const isNarrowing = COOKIE_CATEGORIES.some((category) => previousChoices[category.key] && !nextChoices[category.key])
    if (isNarrowing) {
      window.location.reload()
      return
    }
    initializeAndTrack(location)
  }

  return (
    <>
      <CookieConsent
        disableStyles
        visible='byCookieValue'
        cookieName={COOKIE_CATEGORIES[0].cookieName}
        enableDeclineButton
        flipButtons
        buttonText='Accept'
        declineButtonText='Decline'
        buttonClasses={BANNER_BUTTON_CLASSES}
        declineButtonClasses={BANNER_BUTTON_CLASSES}
        containerClasses='absolute z-50 m-4 flex max-w-lg flex-row flex-wrap items-center justify-center gap-4 rounded bg-white p-8 text-center text-lg text-brand-blue right-0 bottom-0 md:m-8 shadow-xl'
        contentClasses='contents'
        overlay
        overlayClasses='fixed inset-0 z-40 bg-black/60'
        buttonWrapperClasses='order-2 flex flex-wrap items-center justify-center gap-2'
        onAccept={() => applyChoices(allChoices(true))}
        onDecline={() => applyChoices(allChoices(false))}>
        <p className='order-1 w-full'>
          We use cookies to collect information about how you use {process.env.GATSBY_COMPANY_NAME}. We use this information to make the website work as well as possible and improve our services.
          Please see our{' '}
          <Link to='/privacy-policy/' className='underline'>
            Privacy Policy
          </Link>{' '}
          for more information.
        </p>
        <button
          type='button'
          className={`${BANNER_SECONDARY_BUTTON_CLASSES} order-3 ml-6`}
          onClick={() => {
            setChoices(readChoices())
            setShowPreferences(true)
          }}>
          Manage preferences
        </button>
      </CookieConsent>
      {showPreferences && (
        <CookiePreferencesModal
          choices={choices}
          onChange={(key, value) => setChoices((prev) => ({ ...prev, [key]: value }))}
          onSave={() => applyChoices(choices)}
          onClose={() => setShowPreferences(false)}
        />
      )}
    </>
  )
}

CookiePreferencesBanner.propTypes = {
  location: PropTypes.object
}

export default CookiePreferencesBanner
